import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  endpoints,
  type AdminStatus,
  type SynchronizationSource,
  type SynchronizationSummary,
} from '../api';
import { Button, EmptyState, ErrorState, LoadingState, Note, Page, Section, Stat, Stats, Status, dateTime } from '../components';

const POLL_INTERVAL_MS = 10_000;

type Notice = { tone: 'info' | 'success' | 'error'; text: string };

export function SynchronizationPage() {
  const [status, setStatus] = useState<AdminStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const mounted = useRef(false);
  const requestInFlight = useRef(false);

  const loadStatus = useCallback(async (visible = false) => {
    if (requestInFlight.current) return;
    requestInFlight.current = true;
    if (visible && mounted.current) setRefreshing(true);
    try {
      const next = await endpoints.adminStatus();
      if (!mounted.current) return;
      setStatus(next);
      setError(null);
    } catch (caught) {
      if (!mounted.current) return;
      setError(caught instanceof Error ? caught : new Error('No se pudo consultar la sincronización'));
    } finally {
      requestInFlight.current = false;
      if (mounted.current) {
        setLoading(false);
        if (visible) setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void loadStatus();
    const timer = window.setInterval(() => void loadStatus(), POLL_INTERVAL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [loadStatus]);

  const sources = status?.syncHealth ?? [];
  const summary = useMemo(() => status ? status.sync ?? summarizeLegacySources(sources, status) : null, [sources, status]);
  const authorizationRequired = sources.some((source) => source.status === 'authorization_required');
  const progress = summary?.total ? Math.round((summary.current / summary.total) * 100) : 0;
  const deferredCount = summary ? Math.max(0, summary.total - summary.current - summary.pending - summary.stale - summary.failed - summary.missing) : 0;

  async function requestFullSync() {
    setRequesting(true);
    setNotice(null);
    try {
      const result = await endpoints.startSync('all');
      if (!mounted.current) return;
      setNotice({ tone: 'info', text: `Solicitud encolada; todavía no ha terminado. ${result.detail.replace('El worker', 'El sistema')}` });
      await loadStatus();
    } catch (caught) {
      if (mounted.current) setNotice({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo solicitar la sincronización' });
    } finally {
      if (mounted.current) setRequesting(false);
    }
  }

  async function reconnectPilot() {
    setConnecting(true);
    setNotice(null);
    try {
      const result = await endpoints.startEveAuth();
      window.location.assign(result.authorizeUrl);
    } catch (caught) {
      if (mounted.current) {
        setNotice({ tone: 'error', text: caught instanceof Error ? caught.message : 'No se pudo iniciar la reconexión con EVE' });
        setConnecting(false);
      }
    }
  }

  const syncBusy = requesting || Boolean(summary?.requestPending) || summary?.workerStatus === 'running';

  return <Page title="Sincronización" actions={<>
    <Button disabled={refreshing} variant="ghost" onClick={() => void loadStatus(true)}>{refreshing ? 'Comprobando…' : 'Comprobar'}</Button>
    {authorizationRequired
      ? <Button variant="secondary" disabled={connecting} onClick={() => void reconnectPilot()}>{connecting ? 'Abriendo EVE…' : 'Reconectar piloto'}</Button>
      : null}
    {summary && <Button disabled={syncBusy || !summary.workerAlive} onClick={() => void requestFullSync()}>{requesting ? 'Encolando…' : summary.requestPending ? 'En cola' : summary.workerStatus === 'running' ? 'Sincronizando…' : hasProblems(summary) ? 'Reintentar todo' : 'Actualizar todo'}</Button>}
  </>}>
    {notice && <Note tone={notice.tone}>{notice.text}</Note>}
    {loading && !status && <LoadingState />}
    {error && !status && <ErrorState error={error} onRetry={() => void loadStatus(true)} />}
    {error && status && <Note tone="error">No se pudo actualizar el estado. Se mantienen los últimos datos: {error.message}</Note>}

    {status && summary && <>
      {!summary.workerAlive && <Note tone="error"><strong>El motor de sincronización no está disponible.</strong> Revisa el servicio antes de pedir otra sincronización.</Note>}
      <Stats>
        <Stat label="Vigentes" value={`${summary.current} de ${summary.total}`} detail={`${progress} %`} />
        <Stat label="Pendientes" value={String(summary.pending + deferredCount)} detail="O a la espera de ESI" />
        <Stat label="Caducadas" value={String(summary.stale)} tone={summary.stale ? 'warn' : undefined} />
        <Stat label="Con error o ausentes" value={String(summary.failed + summary.missing)} tone={summary.failed + summary.missing ? 'neg' : undefined} />
        <Stat label="Última sincronización" value={<span style={{ fontSize: 15 }}>{dateTime(status.lastSyncAt)}</span>} detail={summary.retryAt ? `Reintento ${dateTime(summary.retryAt)}` : undefined} />
      </Stats>
      <p className="fine">{summary.detail} Las cachés de pocos segundos pueden quedar pendientes entre ciclos aunque todo funcione. Esta página se comprueba sola cada 10 segundos.</p>

      <Section title="Servicios">
        <div className="table-wrap"><table className="table--stack"><thead><tr><th>Servicio</th><th>Estado</th><th>Detalle</th><th>Última actividad</th></tr></thead><tbody>
          <ServiceRow name="Motor de sincronización" status={workerState(summary)} detail={workerDetail(summary)} date={summary.lastCycleAt} />
          <ServiceRow name="EVE ESI" status={status.esi.status} detail={status.esi.detail ?? '—'} date={status.esi.lastSuccessAt} />
          <ServiceRow name="Solicitud manual" status={summary.requestPending || summary.workerStatus === 'running' ? 'pending' : 'current'} detail={summary.requestPending ? 'En cola; aún no se ha completado' : summary.workerStatus === 'running' ? 'Procesándose' : 'Ninguna en espera'} date={summary.lastRequestedAt} />
        </tbody></table></div>
      </Section>

      <Section title="Fuentes">
        {!sources.length
          ? <EmptyState title="Sin fuentes registradas" detail=" Aparecerán cuando empiece la primera sincronización." />
          : <div className="table-wrap"><table className="table--stack"><thead><tr><th>Fuente</th><th>Estado</th><th>Último dato</th><th>Válido hasta</th><th>Detalle</th></tr></thead><tbody>
            {sources.map((source) => <SourceRow key={source.key} source={source} />)}
          </tbody></table></div>}
      </Section>
    </>}
  </Page>;
}

function ServiceRow({ name, status, detail, date }: { name: string; status: string; detail: string; date: string | null }) {
  return <tr>
    <td className="wide"><strong>{name}</strong></td>
    <td data-label="Estado"><SyncStatus value={status} /></td>
    <td className="wide muted" data-label="Detalle">{detail}</td>
    <td className="muted" data-label="Última actividad" style={{ whiteSpace: 'nowrap' }}>{dateTime(date)}</td>
  </tr>;
}

function SourceRow({ source }: { source: SynchronizationSource }) {
  const explanation = source.detail || source.error || defaultSourceDetail(source.status);
  const nextStep = source.action && source.action !== explanation ? source.action : null;
  return <tr>
    <td className="wide"><strong>{source.label && source.label !== source.key ? source.label : sourceName(source.key)}</strong>{source.scope && <small>{scopeName(source.scope)}</small>}</td>
    <td data-label="Estado"><SyncStatus value={source.status} /></td>
    <td className="muted" data-label="Último dato" style={{ whiteSpace: 'nowrap' }}>{dateTime(source.fetchedAt)}</td>
    <td className="muted" data-label={source.retryAt ? 'Reintento' : 'Válido hasta'} style={{ whiteSpace: 'nowrap' }}>{source.retryAt ? <>Reintento {dateTime(source.retryAt)}</> : dateTime(source.expiresAt)}</td>
    <td className="wide muted" data-label="Detalle">{explanation}{nextStep && <small>{nextStep}</small>}</td>
  </tr>;
}

function SyncStatus({ value }: { value: string }) {
  return <Status value={value} label={statusName(value)} />;
}

function statusName(status: string) {
  const labels: Record<string, string> = {
    current: 'Vigente', pending: 'Pendiente', running: 'En curso', stale: 'Caducado', error: 'Error', missing: 'Sin datos',
    authorization_required: 'Reconexión necesaria', deferred: 'En espera de ESI', degraded: 'Requiere atención',
    unavailable: 'No disponible', down: 'No disponible', healthy: 'Operativo', ok: 'Operativo',
  };
  return labels[status] ?? status.replaceAll('_', ' ');
}

function sourceName(key: string) {
  if (key === 'public:prices') return 'Precios medios oficiales';
  if (key === 'public:indices') return 'Índices industriales de sistemas';
  if (key === 'public:facilities') return 'Instalaciones industriales públicas';
  if (key.startsWith('market:') && key.endsWith(':orders')) return 'Órdenes del mercado regional';
  if (key.startsWith('market:') && key.endsWith(':history')) return 'Historial del mercado regional';
  if (key.includes(':planet:')) return 'Detalles de colonia planetaria';
  const module = key.split(':').slice(2).join(':');
  const labels: Record<string, string> = {
    identity: 'Identidad del piloto', skills: 'Skills del piloto', skillqueue: 'Cola de skills',
    attributes: 'Atributos del piloto', wallet: 'Saldo del monedero', 'wallet/journal': 'Diario del monedero',
    'wallet/transactions': 'Transacciones del monedero', orders: 'Órdenes del piloto', assets: 'Activos del piloto',
    blueprints: 'Blueprints del piloto', 'industry/jobs': 'Jobs industriales', planets: 'Colonias planetarias',
    location: 'Ubicación del piloto', ship: 'Nave activa', mining: 'Registro de minería',
  };
  return labels[module] ?? 'Fuente de datos de EVE';
}

function scopeName(scope: string) {
  return ({ character: 'Datos del piloto', market: 'Mercado', public: 'Datos públicos' } as Record<string, string>)[scope] ?? 'EVE ESI';
}

function defaultSourceDetail(status: string) {
  if (status === 'current') return 'No requiere ninguna acción.';
  if (status === 'pending') return 'El sistema todavía está procesando esta fuente.';
  if (status === 'authorization_required') return 'La autorización del piloto ya no permite consultar esta fuente.';
  if (status === 'deferred') return 'ESI ha pedido esperar antes del próximo intento.';
  if (status === 'missing') return 'Aún no se ha recibido ningún dato.';
  return 'La fuente necesita una nueva actualización.';
}

function hasProblems(summary: SynchronizationSummary) {
  return summary.stale + summary.failed + summary.missing > 0;
}

function workerState(summary: SynchronizationSummary) {
  if (!summary.workerAlive) return 'unavailable';
  if (summary.workerStatus === 'running') return 'pending';
  if (summary.workerStatus === 'error') return 'error';
  return 'current';
}

function workerDetail(summary: SynchronizationSummary) {
  if (!summary.workerAlive) return 'Sin actividad reciente; los datos no se actualizarán solos';
  if (summary.workerStatus === 'running') return 'Ciclo en curso; el resultado aún no está confirmado';
  if (summary.workerStatus === 'error') return 'El último ciclo terminó con error; revisa las fuentes afectadas';
  return 'Activo y atendiendo solicitudes';
}

function summarizeLegacySources(sources: SynchronizationSource[], status: AdminStatus): SynchronizationSummary {
  const count = (value: string) => sources.filter((source) => source.status === value).length;
  const current = count('current');
  const pending = count('pending');
  const stale = count('stale');
  const missing = count('missing');
  const failed = sources.filter((source) => ['error', 'authorization_required'].includes(source.status)).length;
  return {
    status: failed || stale || missing ? 'degraded' : pending ? 'pending' : current && current === sources.length ? 'current' : 'deferred',
    current, pending, stale, failed, missing, total: sources.length,
    workerAlive: status.scheduler.status !== 'unavailable' && status.scheduler.status !== 'down',
    workerStatus: 'unknown', lastCycleAt: status.scheduler.lastSuccessAt,
    lastRequestedAt: null, requestPending: false, retryAt: null,
    detail: sources.length ? 'El servidor aún no ofrece el resumen del ciclo; se muestran los estados confirmados de cada fuente.' : 'Todavía no hay información de sincronización.',
  };
}
